import React, { createContext, useContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const TourContext = createContext(null);

export const TOURS = {
  bankAccountSetup: {
    id: "bankAccountSetup",
    label: "Bank Account Setup",
    steps: [
      {
        path: "/direct-deposit",
        selector: '[data-tour="dd-page-title"]',
        title: "Direct Deposit",
        content:
          "This is where you manage how your salary gets paid out — one bank account and one mobile money account.",
        placement: "bottom",
      },
      {
        path: "/direct-deposit",
        selector: '[data-tour="dd-add-account"]',
        title: "Add an account",
        content:
          "Click here to add your bank or mobile money details. You can have one of each on file.",
        placement: "top",
      },
      {
        path: "/direct-deposit",
        selector: '[data-tour="dd-info-note"]',
        title: "Good to know",
        content:
          "Changes to your account only take effect from next month's payroll run.",
        placement: "top",
      },
    ],
  },
  fullEarningView: {
    id: "fullEarningView",
    label: "Full Earning View",
    steps: [
      {
        path: "/salary-metrics-chart",
        selector: '[data-tour="sm-bank-select"]',
        title: "Choose an account",
        content:
          "Salary details are shown per bank account — pick the one you want to inspect.",
        placement: "bottom",
      },
      {
        path: "/salary-metrics-chart",
        selector: '[data-tour="sm-month-select"]',
        title: "Pick a month",
        content: "Switch months to see how your pay broke down in a given period.",
        placement: "bottom",
      },
      {
        path: "/salary-metrics-chart",
        selector: '[data-tour="sm-chart"]',
        title: "Your pay breakdown",
        content:
          "Gross pay, tax, NSSF, housing levy, SHA and net pay, all in one view.",
        placement: "top",
      },
      {
        path: "/salary-metrics-chart",
        selector: '[data-tour="sm-send-payslip"]',
        title: "Get your payslip",
        content:
          "Send a copy of your payslip for the selected period straight to your email.",
        placement: "left",
      },
    ],
  },
  vacationRequest: {
    id: "vacationRequest",
    label: "Vacation Request",
    steps: [
      {
        path: "/vacation",
        selector: '[data-tour="vac-stats"]',
        title: "Your balance",
        content: "Here's how many vacation days you've used, and how many are left.",
        placement: "bottom",
      },
      {
        path: "/vacation",
        selector: '[data-tour="vac-request-button"]',
        title: "Request time off",
        content: "Click here to open the request form, choose your dates, and submit.",
        placement: "left",
      },
      {
        path: "/vacation",
        selector: '[data-tour="vac-history"]',
        title: "Track your requests",
        content:
          "Once submitted, your request shows up here with its approval status.",
        placement: "top",
      },
    ],
  },
  metricsView: {
    id: "metricsView",
    label: "Metrics View",
    steps: [
      {
        path: "/employee-metrics",
        selector: '[data-tour="em-stats"]',
        title: "Your stats at a glance",
        content:
          "Story points, completed tasks, and today's date — your cycle in one row.",
        placement: "bottom",
      },
      {
        path: "/employee-metrics",
        selector: '[data-tour="em-bonus-tracker"]',
        title: "Bonus tracker",
        content:
          "See how close you are to this cycle's bonus target, and what's still needed.",
        placement: "left",
      },
      {
        path: "/employee-metrics",
        selector: '[data-tour="em-add-task"]',
        title: "Log your work",
        content: "Add a Jira task here to keep your story points and history up to date.",
        placement: "bottom",
      },
    ],
  },
  kraPinSetup: {
    id: "kraPinSetup",
    label: "KRA Pin Setup",
    steps: [
      {
        path: "/profile",
        selector: '[data-tour="kra-pin-title"]',
        title: "KRA PIN Section",
        content: "This is where you manage your KRA PIN details. Your KRA PIN is required for tax compliance and payroll processing.",
        placement: "bottom",
      },
      {
        path: "/profile",
        selector: '[data-tour="kra-pin-edit-button"]',
        title: "Edit KRA PIN",
        content: "Click here to edit your KRA PIN number or upload a new document.",
        placement: "left",
      },
      {
        path: "/profile",
        selector: '[data-tour="kra-pin-number-field"]',
        title: "KRA PIN Number",
        content: "Enter your valid KRA PIN number. Format should be: A123456789Z (1 letter, 9 digits, 1 letter).",
        placement: "top",
      },
      {
        path: "/profile",
        selector: '[data-tour="kra-pin-upload-area"]',
        title: "Upload Document",
        content: "Upload a copy of your KRA PIN certificate. PDF, PNG, or JPEG files up to 5MB are accepted.",
        placement: "top",
      },
      {
        path: "/profile",
        selector: '[data-tour="kra-pin-save-button"]',
        title: "Save Your KRA PIN",
        content: "After entering your PIN and uploading the document, click Save to update your profile.",
        placement: "bottom",
      },
    ],
  },
};

export const TourProvider = ({ children }) => {
  const navigate = useNavigate();
  const [activeTourId, setActiveTourId] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);

  const activeTour = activeTourId ? TOURS[activeTourId] : null;
  const currentStep = activeTour ? activeTour.steps[stepIndex] : null;

  const goToStep = useCallback(
    (tour, index) => {
      const step = tour.steps[index];
      if (!step) return;
      setStepIndex(index);
      if (window.location.pathname !== step.path) {
        navigate(step.path);
      }
    },
    [navigate]
  );

  const stopTour = useCallback(() => {
    setActiveTourId(null);
    setStepIndex(0);
  }, []);

  const startTour = useCallback(
    (tourId) => {
      const tour = TOURS[tourId];
      if (!tour) return;
      setActiveTourId(tourId);
      goToStep(tour, 0);
    },
    [goToStep]
  );

  const nextStep = useCallback(() => {
    if (!activeTour) return;
    const next = stepIndex + 1;
    if (next >= activeTour.steps.length) {
      stopTour();
      return;
    }
    goToStep(activeTour, next);
  }, [activeTour, stepIndex, goToStep, stopTour]);

  const prevStep = useCallback(() => {
    if (!activeTour) return;
    const prev = stepIndex - 1;
    if (prev < 0) return;
    goToStep(activeTour, prev);
  }, [activeTour, stepIndex, goToStep]);

  const value = {
    activeTour,
    currentStep,
    stepIndex,
    totalSteps: activeTour ? activeTour.steps.length : 0,
    startTour,
    nextStep,
    prevStep,
    stopTour,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used within a TourProvider");
  return ctx;
};